import {
  NonFilterParams,
  SearchFilterKeys,
  searchFilterKeys,
  SearchFilters,
  searchParamKeys,
} from '@retail-common/util-search-param-types'
import { getOrderedParsedFilterMap } from '../get-ordered-parsed-filter-map/get-ordered-parsed-filter-map'
import { truncateSegmentsToQueryParams } from '../truncate-segments-to-query-params/truncate-segments-to-query-params'
import { filterToMapReducer } from './param-to-map-reducer'
import { truncateVehicleYearSegmentToUrlParams } from './truncate-vehicle-year-segment-to-url-params'

export interface ConvertSearchParamsToUrlConfig {
  defaultSortingCriteria: number
  defaultPageSize: number
  basePath: string
  isPriceUrlSegmentEnabled?: boolean
}

export function convertSearchParamsToUrl(
  params: Partial<SearchFilters & NonFilterParams>,
  {
    defaultSortingCriteria,
    defaultPageSize,
    basePath,
    isPriceUrlSegmentEnabled = false,
  }: ConvertSearchParamsToUrlConfig,
  originalQueryParams: URLSearchParams = new URLSearchParams(
    window.location.search,
  ),
): string {
  // pass the config into filterMapReducer so it uses the correct default values
  const reducer = filterToMapReducer(
    {
      defaultPageSize,
      defaultSortingCriteria,
    },
    isPriceUrlSegmentEnabled,
  )
  // remove un-set filters/params & convert to map of filter key/values & urlSearchParams
  const { urlPathMap, urlSearchParams } = Object.entries(params).reduce(
    (input, [key, value]) => reducer(params, input, [key, value]),
    {
      urlPathMap: new Map<
        string,
        { searchApiValue: string; urlSegmentValue: string }
      >(),
      urlSearchParams: new URLSearchParams(),
    },
  )

  // sort segments
  const { orderedParsedFilterMap: sortedParsedPath } =
    getOrderedParsedFilterMap(urlPathMap)

  // Check if we need to handle vehicleYear special case
  // If vehicleYear is present and we have more than 3 segments, we want to convert it to query params
  if (sortedParsedPath.has('vehicleYear') && sortedParsedPath.size > 3) {
    truncateVehicleYearSegmentToUrlParams(sortedParsedPath, urlSearchParams)
  }

  // Same fallback as vehicleYear: price is dropped first at a fourth segment, so convert it back
  // to toPrice rather than lose the filter.
  const priceSegmentKey = (['monthlyPrice', 'totalPrice'] as const).find(
    (segmentKey) => sortedParsedPath.has(segmentKey),
  )
  if (priceSegmentKey && sortedParsedPath.size > 3) {
    const priceValue = sortedParsedPath.get(priceSegmentKey)?.urlSegmentValue
    if (priceValue) {
      urlSearchParams.set('toPrice', priceValue)
      sortedParsedPath.delete(priceSegmentKey)
    }
  }

  // truncate segments to 3 & convert extras to query parameters
  const {
    truncatedOrderedParsedPath: trimmedSortedParsedPath,
    searchQueryParams,
  } = truncateSegmentsToQueryParams({
    orderedParsedFilterMap: sortedParsedPath,
    maxAllowedSegments: 3,
  })

  // append searchQueryParams to urlSearchParams
  searchQueryParams.forEach((_, key) => {
    if (searchFilterKeys.includes(key as SearchFilterKeys)) {
      // get the value from the original filters rather than the search params, as we don't want the
      // transformed for url segment value in search params, we want the raw one
      const searchParamValue = params[key as keyof SearchFilters]
      if (searchParamValue) {
        urlSearchParams.set(key, searchParamValue.join(','))
      }
    }
  })

  // Add any existing non-filter URL search params
  originalQueryParams.forEach((value, key) => {
    if (!searchParamKeys.includes(key as SearchFilterKeys)) {
      urlSearchParams.set(key, value)
    }
  })

  /*
   * The path already implies financeType=any. Checked against the final path: if the segment was
   * demoted, financeType is all that marks the search as monthly and must stay.
   */
  if (
    trimmedSortedParsedPath.has('monthlyPrice') &&
    urlSearchParams.get('financeType') === 'any'
  ) {
    urlSearchParams.delete('financeType')
  }

  // sort the URL search params to maintain a stable order
  urlSearchParams.sort()

  // combine trimmedSortedParsedPath into a URL
  const urlPathSegments = [...trimmedSortedParsedPath.values()]

  // convert slashes into dashes to prevent segment overloading
  const sanitisedUrlPathSegments = urlPathSegments.map((s) =>
    s.searchApiValue.replace(/\//g, '-').replace(/\[/g, '(').replace(/]/g, ')'),
  )

  // Join the segments, prepending a slash if we've got path segments
  let urlPath = sanitisedUrlPathSegments.join('/')
  if (urlPath.length > 0) {
    urlPath = `/${urlPath}`
  }

  // if we've got any search params, add them
  if ([...urlSearchParams.values()].length > 0) {
    urlPath += `?${urlSearchParams}`
  }

  // in cinch, we had a basePath, so didn't need to prefix /used-cars, but that's not the case elsewhere
  return `${basePath ?? ''}${urlPath}`
}
